import {
  cn,
  Dialog,
  DialogBackdrop,
  DialogContent,
} from '@lemonenergy/lemonpie-3'
import { Close } from '@lemonenergy/lemonpie-icons'
import type { LinksFunction, LoaderFunctionArgs } from '@remix-run/node'
import { json, redirect } from '@remix-run/node'
import {
  Link,
  useLoaderData,
  useNavigate,
  useOutlet,
  useRouteLoaderData,
  useSearchParams,
} from '@remix-run/react'

import Toast from '../../components/Toast/Toast'
import { getAuthorizedUserFromRequest } from '../../controllers.server/auth'
import { createCognitoSSOFormMockedHandler } from '../../utils/auth.util/auth.mock'
import { makeSSOAuthenticationForm } from '../../utils/auth.util/auth.service.server'

import { AuthPage } from './components/AuthPage'
import { GoogleButton } from './components/GoogleButton'

export { default as ErrorBoundary } from '../../components/ErrorBoundary'

export const links: LinksFunction = () => {
  return [
    {
      href: 'https://fonts.googleapis.com/css2?family=Roboto:wght@500&display=swap',
      rel: 'stylesheet',
    },
  ]
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
  if (!process.env.COGNITO_BASE_URL) {
    throw new Error('COGNITO_BASE_URL is not defined')
  }
  if (!process.env.COGNITO_CLIENT_ID) {
    throw new Error('COGNITO_CLIENT_ID is not defined')
  }
  if (!process.env.COGNITO_CLIENT_SCOPE) {
    throw new Error('COGNITO_CLIENT_SCOPE is not defined')
  }

  const url = new URL(request.url)
  const redirectTo = url.searchParams.get('redirectTo') || '/'

  const ssoCallbackUrl = new URL(process.env.BASE_URL || '')
  ssoCallbackUrl.pathname = '/login/callback'

  const user = await getAuthorizedUserFromRequest(request)

  if (user) {
    return redirect(redirectTo)
  }

  const cognitoSSOForm = makeSSOAuthenticationForm({
    provider: 'Google',
    cognitoBaseUrl: process.env.COGNITO_BASE_URL,
    cognitoClientId: process.env.COGNITO_CLIENT_ID,
    ssoClientScope: process.env.COGNITO_CLIENT_SCOPE,
    ssoCallbackUrl: ssoCallbackUrl.toString(),
    successRedirectPath: redirectTo,
  })

  return json({ cognitoSSOForm })
}

const Login = () => {
  const rootData = useRouteLoaderData('root') as {
    ENABLE_MOCKED_SERVICE_WORKER: boolean
  }
  const loaderData = useLoaderData<typeof loader>()

  const outlet = useOutlet()
  const [searchParams] = useSearchParams()

  const navigate = useNavigate()
  const handleSubmit = rootData.ENABLE_MOCKED_SERVICE_WORKER
    ? createCognitoSSOFormMockedHandler(loaderData.cognitoSSOForm, navigate)
    : undefined

  const error = searchParams.get('error')

  const redirectTo = encodeURIComponent(searchParams.get('redirectTo') || '')
  const loginPath = '/login' + (redirectTo ? `?redirectTo=${redirectTo}` : '')
  const partnerLoginPath =
    '/login/parceiro' + (redirectTo ? `?redirectTo=${redirectTo}` : '')

  const navigateToLogin = () => navigate(loginPath, { replace: true })

  return (
    <>
      <AuthPage>
        <div className="grow grid grid-cols-12 items-center justify-center min-w-full">
          <div
            className={cn(
              'col-span-12  rounded-smaller py-[2.5rem] px-larger bg-main fg-expressive-strong text-center',
              'sm:col-span-8 sm:col-start-3 md:col-span-6 md:col-start-4 lg:col-span-4 lg:col-start-5',
            )}
          >
            <h4 className="typography-subheading font-semibold mb-larger">
              Login
            </h4>
            <form
              action={loaderData.cognitoSSOForm.action}
              onSubmit={handleSubmit}
              className="text-center flex justify-center"
            >
              {Object.entries(loaderData.cognitoSSOForm.fields).map(
                ([key, value]) => {
                  return (
                    <input
                      key={key}
                      type="hidden"
                      name={key}
                      value={value || ''}
                      hidden
                    />
                  )
                },
              )}
              <GoogleButton data-testid="auth-button" type="submit">
                Entrar com Google
              </GoogleButton>
            </form>

            <div className="flex items-center gap-smaller my-larger">
              <hr className="grow bdr-soft" />
              <span className="typography-caption fg-soft">ou</span>
              <hr className="grow bdr-soft" />
            </div>

            <Link
              to={partnerLoginPath}
              data-testid="partner-login-link"
              className="typography-body-small font-semibold underline"
            >
              Entrar com e-mail e senha
            </Link>
          </div>
        </div>
      </AuthPage>
      <Dialog
        onClose={navigateToLogin}
        open={Boolean(outlet)}
        data-testid="authentication-error-dialog"
      >
        <DialogBackdrop>
          <DialogContent>{outlet}</DialogContent>
        </DialogBackdrop>
      </Dialog>
      <Toast
        onClose={navigateToLogin}
        isOpen={error === 'authorization_required'}
        data-testid="authorization-error-toast"
      >
        <p className="typography-body-small font-semibold">
          Não autorizado! Refaça o login.
        </p>
        <button onClick={navigateToLogin}>
          <Close size="small" />
        </button>
      </Toast>
    </>
  )
}

export default Login
